import { isAddress } from 'viem';
import { truncateAddress } from '@/lib/format/address';
import { listInWords } from '@/lib/format/list';
import { formatDate } from '@/lib/format/date-time/format-date';
import { formatPercent } from '@/lib/format/percent/percent';
import { formatUsdFigure } from '@/lib/format/usd/usd-figure';

import type {
  ExcludedFeeEvent,
  InvestorFeesResponse,
  PositionPendingFees
} from '../types/api';

/**
 * Investor copy for each fee data code served on /fees.
 *
 * The served message is backend text ("Fee mint events excluded from every
 * total (no usable total-supply snapshot …) for vault(s): 0x8f7a…") and is
 * never shown (QA 2026-10-04 found it printed verbatim). The one thing in
 * it the reader needs, which vaults, is read out of it and named by the
 * position's own name, or a short address for a vault the investor does not
 * hold. `vaults` is that list in words, or null when the message names none.
 */
const FEE_NOTE_BY_CODE: {
  code: string;
  note: (vaults: string | null) => string;
}[] = [
  {
    code: 'FEE_ATTRIBUTION_DEGRADED',
    note: (vaults) =>
      `We can't value some fee charges${vaults ? ` on ${vaults}` : ''} yet, so we left them out of these totals.`
  },
  {
    code: 'FEES_COVERAGE_UNVERIFIED',
    note: (vaults) =>
      `We could not verify every past fee charge on ${vaults ?? 'one of your vaults'}, so older charges may be missing from these totals.`
  },
  {
    code: 'PROTOCOL_SPLIT_CONFIG_DERIVED',
    note: () =>
      'The protocol share of some fees comes from configuration rather than exact per event data.'
  },
  {
    // v1.61.0. Not the same as CONFIG_DERIVED: there the split is known from
    // configuration, here it is not known at all, and the whole amount is
    // booked to the vault by stated convention. That skews what the reader
    // is looking at, so the direction of the skew is named.
    code: 'PROTOCOL_SPLIT_UNKNOWN',
    note: (vaults) =>
      `For ${vaults ?? 'some vaults'}, we can't see how fees split with the protocol, so the whole amount counts as the vault’s own fee. Protocol fees here may read low and vault fees high.`
  },
  {
    code: 'WINDOW_TOO_SHORT',
    note: () =>
      'This window is short, so the ratios can move a lot as days are added.'
  }
];

/** The warning whose vaults `excludedEvents` (API 1.157) lists event by event. */
const EXCLUDED_EVENTS_CODE = 'FEE_ATTRIBUTION_DEGRADED';

/** A code this build does not know yet: said plainly, never passed through. */
const UNKNOWN_FEE_NOTE = 'Some fee figures here may be incomplete.';

/**
 * A whole address in served text: bounded on both sides, so the first 40
 * hex digits of a 64 digit transaction hash are never read as a vault.
 */
const ADDRESS = /\b0x[0-9a-fA-F]{40}\b/g;

/** At most this many vaults are named in one sentence; the rest are counted. */
const MAX_NAMED_VAULTS = 3;

/**
 * Distinct valid addresses, lowercase, in the order given. Checked for
 * shape, not checksum: served addresses are lowercase and compared so.
 */
function addressesIn(values: readonly unknown[]): string[] {
  const found = values.filter(
    (value): value is string =>
      typeof value === 'string' && isAddress(value, { strict: false })
  );
  return [...new Set(found.map((value) => value.toLowerCase()))];
}

/**
 * The vaults a warning is about. The served `context.vaults` list first
 * (API 1.157 adds it; parsed tolerantly, keeping only valid addresses),
 * else the addresses written into the served text.
 */
function warningVaults(warning: FeeWarning): string[] {
  if (typeof warning !== 'string') {
    const served = warning.context?.vaults;
    if (Array.isArray(served)) {
      const listed = addressesIn(served);
      if (listed.length > 0) return listed;
    }
  }
  const message = typeof warning === 'string' ? warning : warning.message;
  return addressesIn([...message.matchAll(ADDRESS)].map((match) => match[0]));
}

type FeeWarning = InvestorFeesResponse['warnings'][number];

/** Each held vault's own name, by lowercase address. */
function vaultNames(
  positions: InvestorFeesResponse['positions']
): Map<string, string> {
  return new Map(
    positions.map((position) => [
      position.vaultAddress.toLowerCase(),
      position.name
    ])
  );
}

/** A vault by its position's name, or a short address when not held. */
function vaultName(names: Map<string, string>, address: string): string {
  return names.get(address.toLowerCase()) ?? truncateAddress(address);
}

/**
 * One plain sentence per fee code served, in order. A code served more
 * than once is one sentence naming every vault it covers. Vaults are named
 * from the response's own positions, or a short address for a vault the
 * investor does not hold.
 */
export function feeNotes(
  fees: Pick<InvestorFeesResponse, 'warnings' | 'positions'> &
    Partial<Pick<InvestorFeesResponse, 'excludedEvents'>>
): string[] {
  const names = vaultNames(fees.positions);
  // The left out charges are listed one by one (`excludedFeeEventLines`),
  // which says everything the general note did and more. The note stays
  // when some of them could not be read, so the list is never taken for
  // the whole story.
  const listed =
    fees.excludedEvents != null &&
    fees.excludedEvents.rows.length > 0 &&
    fees.excludedEvents.dropped === 0;
  // Insertion order is the served order of each code's first warning.
  const vaultsByCode = new Map<string, Set<string>>();
  let hasUnknown = false;
  for (const warning of fees.warnings) {
    const code = typeof warning === 'string' ? warning : warning.code;
    const known = FEE_NOTE_BY_CODE.find((entry) => code.includes(entry.code));
    if (!known) {
      hasUnknown = true;
      continue;
    }
    if (listed && known.code === EXCLUDED_EVENTS_CODE) continue;
    const vaults = vaultsByCode.get(known.code) ?? new Set<string>();
    for (const address of warningVaults(warning)) vaults.add(address);
    vaultsByCode.set(known.code, vaults);
  }
  const notes = [...vaultsByCode].map(([code, addresses]) => {
    const vaults = [...addresses].map(
      (address) => names.get(address) ?? truncateAddress(address)
    );
    const known = FEE_NOTE_BY_CODE.find((entry) => entry.code === code)!;
    return known.note(
      vaults.length > 0 ? listInWords(vaults, { max: MAX_NAMED_VAULTS }) : null
    );
  });
  return hasUnknown ? [...notes, UNKNOWN_FEE_NOTE] : notes;
}

/**
 * How far a vault's share price sits from its high water mark, the level it
 * must pass before a performance fee is charged again. The served figure is
 * a distance, so the line states it without claiming a direction.
 */
export function highWaterMarkLine(distanceBps: number): string {
  return distanceBps === 0
    ? 'At its high water mark'
    : `${formatPercent(Math.abs(distanceBps), { from: 'bps', digits: 2, sign: 'minus' })} from its high water mark`;
}

// ---------------------------------------------------------------------------
// Pending fees (API 1.157): management and performance fees a vault has
// built up since its last charge and not yet minted. Information beside the
// charged figures, never taken off any of them. The served `reason` codes
// and `message` texts are never shown.
// ---------------------------------------------------------------------------

/**
 * Whether a served ~USD figure, truncated to cents upstream, is below one
 * cent. It is then either nothing at all or a fraction of a cent, which
 * only the fee shares can tell apart.
 */
function underOneCent(value: string): boolean {
  const [whole = '', fraction = ''] = value.split('.');
  return /^0*$/.test(whole) && /^0*$/.test(fraction.slice(0, 2));
}

/** A served ~USD figure of at least one cent, as an approximate amount. */
function approxUsd(value: string): string {
  return `≈ ${formatUsdFigure(value, { maximumFractionDigits: 2 })}`;
}

/**
 * A vault whose charge would mint no fee shares has nothing building up:
 * a real zero. Null shares are not read, which is not zero.
 */
function noFeeShares(pending: PositionPendingFees): boolean {
  return pending.vaultFeeShares != null && /^0+$/.test(pending.vaultFeeShares);
}

/**
 * Each held position's block. Held is every state but `not_held`, a state
 * this build does not know included, and a position with no block of its
 * own (absent, or dropped as malformed): that one is held and unread,
 * `undefined` here, never taken for nothing building up.
 */
function heldBlocks(
  positions: InvestorFeesResponse['positions']
): (PositionPendingFees | undefined)[] {
  return positions.flatMap((position) =>
    position.pendingFees?.state === 'not_held' ? [] : [position.pendingFees]
  );
}

/** A held vault the backend could not read, or sent no readable block for. */
function isUnread(pending: PositionPendingFees | undefined): boolean {
  return pending == null || pending.state === 'unmeasured';
}

interface PendingFeesFigure {
  /** "≈ $0.41", "Under $0.01", "None right now" or "Not measured yet". */
  value: string;
  /** When it was read, or which vaults could not be. */
  detail: string | null;
}

/**
 * The investor's share of the fees building up, summed over held vaults.
 * Null when the backend serves none, and when no position is held (no
 * positions at all, or every one `not_held`): there is then nothing
 * building up for this investor to be told about. The served sum is null
 * whenever one held vault could not be read, so it is never shown short;
 * a served sum is shown even when the positions carry no blocks of their
 * own, since the backend sums every held vault. A sum under a cent is a
 * real zero, "None right now", only when every held vault was measured
 * with no fee shares building up; a vault with no readable block of its
 * own keeps it "Under $0.01".
 */
export function pendingFeesFigure(
  fees: Pick<InvestorFeesResponse, 'pendingFees' | 'positions'>
): PendingFeesFigure | null {
  const pending = fees.pendingFees;
  if (!pending) return null;
  const held = heldBlocks(fees.positions);
  if (held.length === 0) return null;
  if (pending.totalUsd != null) {
    const asOf = pending.asOf
      ? formatDate(pending.asOf, 'date-time', { zone: true })
      : null;
    const nothingBuildingUp = held.every(
      (position) =>
        position != null &&
        position.state === 'measured' &&
        noFeeShares(position)
    );
    return {
      value: !underOneCent(pending.totalUsd)
        ? approxUsd(pending.totalUsd)
        : nothingBuildingUp
          ? 'None right now'
          : 'Under $0.01',
      detail: `${asOf ? `As of ${asOf}. ` : ''}Not in the totals above until charged.`
    };
  }
  // The vaults the backend names, else any held position that could not
  // be read or carries no readable block; with none to name, the sentence
  // still says why there is no total.
  const names = vaultNames(fees.positions);
  const listed = pending.unmeasuredVaults ?? [];
  const addresses =
    listed.length > 0
      ? listed
      : fees.positions
          .filter(
            (position) =>
              position.pendingFees?.state !== 'not_held' &&
              isUnread(position.pendingFees)
          )
          .map((position) => position.vaultAddress);
  const unread = [
    ...new Set(addresses.map((address) => vaultName(names, address)))
  ];
  return {
    value: 'Not measured yet',
    detail: `We couldn't read ${
      unread.length > 0
        ? listInWords(unread, { max: MAX_NAMED_VAULTS })
        : 'some of your vaults'
    } just now, so there is no total yet.`
  };
}

/**
 * One position's line: its share of the fees building up. Nothing for a
 * vault the investor no longer holds or one with no fee shares building
 * up, and "not measured yet" for anything else that carries no amount, a
 * state this build does not know included. `served` says the response
 * carries pending fees at all: then a position with no readable block of
 * its own is held and unread, not quiet. Without it (an older backend),
 * no position has a block and none gets a line.
 */
export function positionPendingLine(
  pending: PositionPendingFees | undefined,
  served: boolean
): string | null {
  if (!pending) return served ? 'Fees building up: not measured yet' : null;
  if (pending.state === 'not_held') return null;
  if (pending.state === 'measured' && pending.totalUsd != null) {
    if (noFeeShares(pending)) return null;
    const amount = underOneCent(pending.totalUsd)
      ? 'Under $0.01'
      : approxUsd(pending.totalUsd);
    return `${amount} building up, not yet charged`;
  }
  return 'Fees building up: not measured yet';
}

/** At most this many left out charges are written out; the rest are counted. */
const MAX_LISTED_EVENTS = 5;

// Maps, not object literals: a served word such as "constructor" must read
// as unknown, never as something every object already has.
const EVENT_KIND = new Map([
  ['MANAGEMENT_FEES_MINTED', 'a management fee charge'],
  ['PERFORMANCE_FEES_MINTED', 'a performance fee charge'],
  ['DEPOSIT_FEE_COLLECTED', 'a deposit fee you paid'],
  ['WITHDRAW_FEE_COLLECTED', 'a withdrawal fee you paid']
]);

const EXCLUDED_REASON = new Map([
  [
    'NO_PRE_EVENT_SUPPLY',
    "We couldn't see how many vault shares there were just before it, so we can't work out your part yet."
  ],
  [
    'ASSET_DECIMALS_UNKNOWN',
    "We don't have the details of the vault's currency yet, so we can't value it."
  ]
]);

/** Why a charge is left out, for a reason this build does not know. */
const UNKNOWN_EXCLUDED_REASON = "We can't value it yet.";

/**
 * An event is its transaction and log index, so a row served twice reads
 * as one line. A row missing either keeps a line of its own.
 */
function eventKey(event: ExcludedFeeEvent, index: number): string {
  return event.txHash != null && event.logIndex != null
    ? `${event.txHash.toLowerCase()}:${event.logIndex}`
    : `row:${index}`;
}

/** One left out charge as a line, keyed by the event it is about. */
interface ExcludedFeeEventLine {
  key: string;
  text: string;
}

/**
 * Each fee charge left out of the totals, in plain words: the vault, the
 * day, what kind of charge and why it can't be valued. Empty when the
 * backend serves none. Never the served message.
 */
export function excludedFeeEventLines(
  fees: Pick<InvestorFeesResponse, 'positions'> &
    Partial<Pick<InvestorFeesResponse, 'excludedEvents'>>
): ExcludedFeeEventLine[] {
  const rows = fees.excludedEvents?.rows ?? [];
  const names = vaultNames(fees.positions);
  const unique = [
    ...new Map(rows.map((event, index) => [eventKey(event, index), event]))
  ];
  const lines = unique.slice(0, MAX_LISTED_EVENTS).map(([key, event]) => {
    const day = event.blockTimestamp
      ? formatDate(event.blockTimestamp, 'day-month-year')
      : null;
    const kind = EVENT_KIND.get(event.eventType ?? '') ?? 'a fee charge';
    const why =
      EXCLUDED_REASON.get(event.reason ?? '') ?? UNKNOWN_EXCLUDED_REASON;
    return {
      key,
      text: `${vaultName(names, event.vaultAddress)}${day ? `, ${day}` : ''}: ${kind}. ${why}`
    };
  });
  const more = unique.length - lines.length;
  return more > 0
    ? [
        ...lines,
        {
          key: 'more',
          text: `And ${more} more ${more === 1 ? 'charge' : 'charges'} like these.`
        }
      ]
    : lines;
}
